import { Camera, X } from "lucide-react";
import type React from "react";
import { useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { EventReminderSetting } from "@/components/common/EventReminderSetting";
import { Button } from "@/components/ui/button";
import {
  EVENT_DESCRIPTION_MAX_LENGTH,
  EVENT_LOCATION_MAX_LENGTH,
  EVENT_LOCATION_TYPE_HYBRID,
  EVENT_LOCATION_TYPE_OFFLINE,
  EVENT_LOCATION_TYPE_ONLINE,
  EVENT_ONLINE_LOCATION_MAX_LENGTH,
} from "@/constants";
import { StepFooter } from "@/features/create-event/components/StepFooter";
import type { StepProps } from "@/features/create-event/types";
import type { ReminderFormData } from "@/features/study-group-events/type";

export const StepAdditionalDetails: React.FC<StepProps> = ({
  formData,
  onChange,
  handleNext,
  handleSaveDraft,
  setStep,
  isSubmitting,
}) => {
  const { t } = useTranslation();
  const isSaveDraftDisabled = !formData.title;
  const [fileError, setFileError] = useState<string | null>(null);
  const locationId = useId();
  const onlineLocationId = useId();
  const descriptionId = useId();
  const fileId = useId();

  // 初期化時にデフォルト値を設定（既存のデータを保持）
  useEffect(() => {
    const updates: Partial<typeof formData> = {};

    if (!formData.location_type) {
      updates.location_type = EVENT_LOCATION_TYPE_ONLINE;
    }

    // remindersが未定義の場合のみデフォルト値を設定
    if (!formData.reminders) {
      updates.reminders = [{ unit: 3, value: 1 }];
    }

    if (Object.keys(updates).length > 0) {
      onChange(updates);
    }
  }, [formData.location_type, formData.reminders, onChange]);

  // 安全なデータ更新関数
  const updateFormData = (updates: Partial<typeof formData>) => {
    onChange(updates);
  };

  // ロケーションタイプ変更ハンドラー
  const handleLocationTypeChange = (type: number) => {
    updateFormData({ location_type: type });
  };

  // ファイル検証用の共通関数
  const validateFile = (file: File): string | null => {
    if (!file.type.match(/^image\/(jpeg|jpg|png)$/)) {
      return "Only PNG and JPEG images are allowed";
    }

    if (file.size > 5 * 1024 * 1024) {
      return "File size must be less than 5MB";
    }

    return null;
  };

  // ファイル入力ハンドラー
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const error = validateFile(file);
    setFileError(error);

    if (!error) {
      updateFormData({ files: [file] });
    }
  };

  // オンラインロケーション入力ハンドラー
  const handleOnlineLocationChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = e.target.value;
    if (value.length <= EVENT_ONLINE_LOCATION_MAX_LENGTH) {
      updateFormData({ online_location: value });
    }
  };

  // 対面ロケーション入力ハンドラー
  const handleInPersonLocationChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = e.target.value;
    if (value.length <= EVENT_LOCATION_MAX_LENGTH) {
      updateFormData({ location: value });
    }
  };

  // 説明文入力ハンドラー
  const handleDescriptionChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    const value = e.target.value;
    if (value.length <= EVENT_DESCRIPTION_MAX_LENGTH) {
      updateFormData({ description: value });
    }
  };

  // リマインダー変更ハンドラー
  const handleReminderChange = (newReminders: ReminderFormData[]) => {
    updateFormData({ reminders: newReminders });
  };

  const currentReminders = formData.reminders ?? [];

  return (
    <div className="flex h-full flex-col gap-6 overflow-y-auto p-6">
      <div className="space-y-4">
        <label
          htmlFor="location"
          className="flex flex-col gap-1 text-sm font-medium"
        >
          {t("common.location")}

          <div className="space-y-2">
            <div className="flex space-x-3">
              <label className="inline-flex cursor-pointer items-center gap-1">
                <input
                  type="radio"
                  name="locationType"
                  value={EVENT_LOCATION_TYPE_ONLINE}
                  checked={
                    formData.location_type === EVENT_LOCATION_TYPE_ONLINE
                  }
                  onChange={() =>
                    handleLocationTypeChange(EVENT_LOCATION_TYPE_ONLINE)
                  }
                  className="rounded-full border-gray-300 text-rw-sub-deep"
                />
                {t("common.online")}
              </label>

              <label className="inline-flex cursor-pointer items-center gap-1">
                <input
                  type="radio"
                  name="locationType"
                  value={EVENT_LOCATION_TYPE_HYBRID}
                  checked={
                    formData.location_type === EVENT_LOCATION_TYPE_HYBRID
                  }
                  onChange={() =>
                    handleLocationTypeChange(EVENT_LOCATION_TYPE_HYBRID)
                  }
                  className="rounded-full border-gray-300 text-rw-sub-deep"
                />
                {t("common.hybrid")}
              </label>

              <label className="inline-flex cursor-pointer items-center gap-1">
                <input
                  type="radio"
                  name="locationType"
                  value={EVENT_LOCATION_TYPE_OFFLINE}
                  checked={
                    formData.location_type === EVENT_LOCATION_TYPE_OFFLINE
                  }
                  onChange={() =>
                    handleLocationTypeChange(EVENT_LOCATION_TYPE_OFFLINE)
                  }
                  className="rounded-full border-gray-300 text-rw-sub-deep"
                />
                {t("common.inPerson")}
              </label>
            </div>

            {(formData.location_type === EVENT_LOCATION_TYPE_OFFLINE ||
              formData.location_type === EVENT_LOCATION_TYPE_HYBRID) && (
              <label
                htmlFor={locationId}
                className="flex flex-col gap-1 text-xs"
              >
                <div className="flex justify-between gap-2">
                  {t("eventCreate.physicalLocation")}
                  <span className="text-xs">
                    {formData.location?.length || 0}/{EVENT_LOCATION_MAX_LENGTH}
                  </span>
                </div>

                <input
                  id={locationId}
                  type="text"
                  placeholder={t("eventCreate.locationPlaceholder")}
                  value={formData.location || ""}
                  onChange={handleInPersonLocationChange}
                  className="w-full rounded border p-2"
                  maxLength={EVENT_LOCATION_MAX_LENGTH}
                />
              </label>
            )}

            {(formData.location_type === EVENT_LOCATION_TYPE_ONLINE ||
              formData.location_type === EVENT_LOCATION_TYPE_HYBRID) && (
              <label
                htmlFor={onlineLocationId}
                className="flex flex-col gap-1 text-xs"
              >
                <div className="flex justify-between">
                  {t("common.eventUrl")}
                  <span className="text-xs">
                    {formData.online_location?.length || 0}/
                    {EVENT_ONLINE_LOCATION_MAX_LENGTH}
                  </span>
                </div>

                <input
                  id={onlineLocationId}
                  type="text"
                  placeholder={t("common.urlPlaceholder")}
                  value={formData.online_location || ""}
                  onChange={handleOnlineLocationChange}
                  className="w-full rounded border p-2"
                  maxLength={EVENT_ONLINE_LOCATION_MAX_LENGTH}
                />
              </label>
            )}
          </div>
        </label>

        <label
          htmlFor={descriptionId}
          className="flex w-full flex-col gap-1 text-sm font-medium"
        >
          <div className="flex justify-between">
            {t("common.description")}
            <span className="text-xs">
              {formData.description?.length || 0}/{EVENT_DESCRIPTION_MAX_LENGTH}
            </span>
          </div>

          <textarea
            id={descriptionId}
            placeholder={t("common.description")}
            value={formData.description || ""}
            onChange={handleDescriptionChange}
            rows={4}
            className="w-full rounded border p-2"
            maxLength={EVENT_DESCRIPTION_MAX_LENGTH}
          />
        </label>

        <label
          htmlFor={fileId}
          className="flex flex-col gap-1 text-sm font-medium"
        >
          {t("common.image")}

          {formData.files?.[0] ? (
            <div className="relative">
              <img
                src={URL.createObjectURL(formData.files[0])}
                alt="Event preview"
                className="h-48 w-full rounded-lg object-cover"
              />
              <div className="absolute right-2 top-2 flex space-x-2">
                <Button
                  variant="ghost"
                  size="mini"
                  onClick={() => {
                    updateFormData({ files: [] });
                    setFileError(null);
                  }}
                  title="Remove image"
                >
                  <X className="size-4" />
                </Button>
                <label
                  className="cursor-pointer rounded-full border border-gray-200 bg-white p-1.5 text-gray-600 shadow-sm transition-colors hover:border-blue-600 hover:text-blue-600"
                  title="Replace image"
                >
                  <Camera className="size-4" />
                  <input
                    id={fileId}
                    type="file"
                    className="hidden"
                    accept="image/png,image/jpeg,image/jpg"
                    onChange={handleFileChange}
                  />
                </label>
              </div>
            </div>
          ) : (
            <label
              htmlFor={fileId}
              className="flex w-full cursor-pointer flex-col items-center gap-2 rounded-lg border-2 border-dashed border-gray-300 p-4 hover:bg-gray-50"
            >
              <Camera />
              <span className="text-sm">{t("common.uploadImage")}</span>
              <span className="text-xs">
                {t("common.uploadImageCondition")}
                <br /> {t("eventCreate.uploadImageRecommendation")}
              </span>
              <input
                id={fileId}
                type="file"
                className="hidden"
                accept="image/png,image/jpeg,image/jpg"
                onChange={handleFileChange}
              />
            </label>
          )}
          {fileError && (
            <div className="mt-2 text-sm text-red-600" role="alert">
              {fileError}
            </div>
          )}
        </label>

        <EventReminderSetting
          reminders={currentReminders}
          onChange={handleReminderChange}
          className="col-span-1"
        />
      </div>

      <StepFooter
        isSubmitting={isSubmitting}
        onBack={() => setStep?.((prev) => Math.max(1, prev - 1))}
        onSaveDraft={handleSaveDraft}
        onNext={handleNext}
        saveDraftDisabled={isSaveDraftDisabled}
      />
    </div>
  );
};
